import type { RateLineListQuery } from '@chukua/common';
import { Segmented, Select } from 'antd';
import { ZoneSearchSelect } from '../../../../zones/public/ZoneSearchSelect';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import { useJobTypeOptions } from '../hooks/use-job-type-options';

type LineSort = Pick<RateLineListQuery, 'sort' | 'order'>;

const SORT_OPTIONS: { value: string; label: string; sort: LineSort }[] = [
  { value: 'validFrom:desc', label: 'Newest first', sort: { sort: 'validFrom', order: 'desc' } },
  { value: 'validFrom:asc', label: 'Oldest first', sort: { sort: 'validFrom', order: 'asc' } },
  { value: 'amountMinor:desc', label: 'Price, high to low', sort: { sort: 'amountMinor', order: 'desc' } },
  { value: 'amountMinor:asc', label: 'Price, low to high', sort: { sort: 'amountMinor', order: 'asc' } },
];

const SORT_BY_VALUE: Record<string, LineSort> = Object.fromEntries(SORT_OPTIONS.map((option) => [option.value, option.sort]));

interface LineFiltersProps {
  list: ListQueryControls<RateLineListQuery>;
  /** What the table is actually asking for, with the tab's default for `current` filled in. */
  isCurrent: boolean;
}

/** Origin, destination and job type narrow the lines on the server, as does the current/superseded toggle and the sort. */
export function LineFilters({ list, isCurrent }: LineFiltersProps) {
  const jobTypes = useJobTypeOptions();
  const { query } = list;

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="w-full sm:w-56">
        <ZoneSearchSelect value={query.originZoneCode?.[0]} onChange={(code) => list.setQuery({ originZoneCode: code ? [code] : undefined })} />
      </div>
      <div className="w-full sm:w-56">
        <ZoneSearchSelect value={query.destinationZoneCode?.[0]} onChange={(code) => list.setQuery({ destinationZoneCode: code ? [code] : undefined })} />
      </div>
      <Select
        className="w-full sm:w-48"
        allowClear
        placeholder="Job type"
        value={query.jobTypeCode?.[0]}
        options={jobTypes.map((type) => ({ value: type.code, label: type.name }))}
        onChange={(code?: string) => list.setQuery({ jobTypeCode: code ? [code] : undefined })}
      />
      <Segmented
        value={isCurrent ? 'current' : 'superseded'}
        options={[
          { value: 'current', label: 'Current' },
          { value: 'superseded', label: 'Superseded' },
        ]}
        onChange={(value) => list.setQuery({ current: value === 'current' })}
      />
      <Select
        className="w-full sm:w-44"
        aria-label="Sort lines"
        value={`${query.sort}:${query.order}`}
        options={SORT_OPTIONS.map(({ value, label }) => ({ value, label }))}
        onChange={(value) => list.setQuery(SORT_BY_VALUE[value])}
      />
    </div>
  );
}
